import { axiosInstance } from "@/lib/axios-instance";
import type { RaiseBoardCommentOrigin } from "@/api/raise-board-api";

/*
 * The internal deal profile's view of a deal's board comments: every scope at
 * once — the public/global thread and each lookbook the deal sits in — rather
 * than the one thread the public endpoints in raise-board-api.ts serve.
 *
 * These are workspace-authenticated, so unlike the public surfaces they name a
 * lookbook by its internal id. A lookbook's public id is a share link; reading
 * the comments doesn't need one, so it isn't handed out here.
 */

export interface DealBoardComment {
  _id: string;
  user_name?: string;
  text: string;
  /** Absent on comments written before origins were recorded. */
  origin?: RaiseBoardCommentOrigin;
  created_on: string;
  /** Null on the shared public/global thread. */
  lookbook_id: string | null;
  lookbook_name: string | null;
  /**
   * Decided server-side. The client holds a Clerk id and the comment's author
   * is a db user id, so it cannot work this out for itself.
   */
  can_delete: boolean;
  /** Left by a signed-out lookbook visitor under a name they typed. */
  guest?: boolean;
}

/** A lookbook holding this deal — i.e. a thread that can be posted into. */
export interface DealBoardLookbook {
  _id: string;
  name: string;
}

interface DealBoardCommentsResponse {
  status: string;
  application_id: string;
  comments: DealBoardComment[];
  lookbooks: DealBoardLookbook[];
}

interface AddDealBoardCommentResponse {
  status: string;
  comment: DealBoardComment;
  comment_count: number;
}

export const dealBoardCommentsApi = {
  list: async (
    opportunityId: string,
    companyId: string
  ): Promise<{
    comments: DealBoardComment[];
    lookbooks: DealBoardLookbook[];
  }> => {
    const response = await axiosInstance.get<DealBoardCommentsResponse>(
      "/deal_board_comments",
      { params: { opportunity_id: opportunityId, company_id: companyId } }
    );
    return {
      comments: response.data?.comments ?? [],
      lookbooks: response.data?.lookbooks ?? [],
    };
  },

  add: async (args: {
    opportunityId: string;
    companyId: string;
    text: string;
    /** Omit to post to the shared public + global thread. */
    lookbookId?: string;
  }): Promise<AddDealBoardCommentResponse> => {
    const response = await axiosInstance.post<AddDealBoardCommentResponse>(
      "/add_deal_board_comment",
      {
        opportunity_id: args.opportunityId,
        company_id: args.companyId,
        text: args.text,
        lookbook_id: args.lookbookId,
      }
    );
    return response.data;
  },
};
